import { useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  CardHeader,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SegmentedControl,
  SkeletonTable,
  StatTile,
  TrendChart,
  cn,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  COST_VIEW,
  PROGRAMS_PARAM,
  PROGRAMS_PATH,
  completenessText,
  formatMoney,
  formatMoneyCompact,
  formatPerDollar,
  formatPerUnit,
  formatPercent,
  quarterLabel,
  readProgramDetail,
  windowText,
  type CategoryCost,
  type CostView,
  type ProgramDetail,
} from '@/lib/program-profitability';
import {
  ContributionNotes,
  EVENT_DEFAULT_SORT,
  ProgramEventsTable,
  ProgramsFilters,
  useProgramsQuery,
} from '@/pages/program-profitability-shared';

const COST_VIEW_OPTIONS = [
  { key: COST_VIEW.total, label: 'Total' },
  { key: COST_VIEW.perParticipant, label: 'Per participant' },
] as const;

function costValue(entry: CategoryCost, view: CostView): number {
  return view === COST_VIEW.perParticipant ? (entry.perParticipant ?? 0) : entry.cost;
}

function CostByCategory({ detail }: { readonly detail: ProgramDetail }): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const view: CostView = params.get(PROGRAMS_PARAM.costView) === COST_VIEW.perParticipant ? COST_VIEW.perParticipant : COST_VIEW.total;

  function setView(next: CostView): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next === COST_VIEW.total) nextParams.delete(PROGRAMS_PARAM.costView);
        else nextParams.set(PROGRAMS_PARAM.costView, next);
        return nextParams;
      },
      { replace: true },
    );
  }

  const format = view === COST_VIEW.perParticipant ? (value: number) => formatPerUnit(value) : formatMoneyCompact;
  return (
    <Panel className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <CardHeader
          title="Where the money goes"
          description="Itemized expense lines by category. Hover a bar for the total, the per-participant cost and the share."
        />
        <SegmentedControl<CostView> label="Cost by category as" value={view} onChange={setView} options={COST_VIEW_OPTIONS} />
      </div>
      <TrendChart
        title="Itemized cost by category"
        unit={view === COST_VIEW.perParticipant ? 'Per participant' : 'Cost'}
        points={detail.costByCategory.map((entry) => ({
          key: entry.category,
          label: entry.category,
          value: costValue(entry, view),
          details: [
            `${formatMoney(entry.cost)} total`,
            `${formatPerUnit(entry.perParticipant)} per participant`,
            `${formatPercent(entry.share)} of itemized cost`,
            `${entry.events} ${entry.events === 1 ? 'event' : 'events'}`,
          ],
        }))}
        formatValue={format}
        height={280}
      />
    </Panel>
  );
}

function MarginByQuarter({ detail }: { readonly detail: ProgramDetail }): React.ReactElement {
  const quarters = detail.marginByQuarter;
  return (
    <Panel className="p-5">
      <CardHeader
        title="Margin by quarter"
        description={`Contribution as a share of revenue, by event quarter. The dashed line is the ${formatPercent(detail.targetMargin)} target.`}
      />
      <TrendChart
        title="Margin by event quarter"
        unit="Margin"
        variant="line"
        points={quarters.map((quarter) => ({
          key: quarter.quarter,
          label: quarterLabel(quarter.quarter),
          value: quarter.margin === null ? Number.NaN : Math.round(quarter.margin * 1000) / 10,
          details: [
            `${quarter.events} ${quarter.events === 1 ? 'event' : 'events'}`,
            `${formatMoney(quarter.contribution)} on ${formatMoney(quarter.revenue)}`,
          ],
        }))}
        overlays={[
          { key: 'target', label: 'Target', values: quarters.map(() => detail.targetMargin * 100), tone: 1 },
        ]}
        formatValue={(value) => `${Math.round(value)}%`}
        height={280}
      />
    </Panel>
  );
}

function DetailTiles({ detail }: { readonly detail: ProgramDetail }): React.ReactElement {
  const metrics = detail.metrics;
  return (
    <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatTile label="Contribution" value={formatMoney(metrics.contribution)} hint={`${metrics.events} events, ${windowText(detail.window)}`} />
      <StatTile label="Margin" value={formatPercent(metrics.margin)} hint={`Median event ${formatPercent(metrics.medianEventMargin)}`} />
      <StatTile
        label="Contribution per participant"
        value={formatPerUnit(metrics.contributionPerParticipant)}
        hint={`Revenue ${formatPerUnit(metrics.revenuePerParticipant)} per participant`}
      />
      <StatTile
        label="Contribution per $1 of revenue"
        value={formatPerDollar(metrics.contributionPerRevenueDollar)}
        hint={completenessText(metrics)}
      />
    </div>
  );
}

/**
 * One program: where its money goes by category (in total and per
 * participant), its margin by quarter, and every event worst margin first.
 */
export function ProgramProfitabilityDetailPage({ groupKey }: { readonly groupKey: string }): React.ReactElement {
  const [params] = useSearchParams();
  const filters = useProgramsQuery();
  const detail = useQuery({
    queryKey: ['accounting-program', groupKey, filters.query],
    queryFn: ({ signal }) => readProgramDetail(groupKey, filters.query, signal),
    placeholderData: keepPreviousData,
  });

  const back = new URLSearchParams();
  for (const key of [PROGRAMS_PARAM.from, PROGRAMS_PARAM.to, PROGRAMS_PARAM.mode]) {
    const value = params.get(key);
    if (value) back.set(key, value);
  }
  const backTo = back.toString() ? `${PROGRAMS_PATH}?${back.toString()}` : PROGRAMS_PATH;
  const hasCosts = (detail.data?.costByCategory.length ?? 0) > 0;
  const hasQuarters = (detail.data?.marginByQuarter.length ?? 0) > 1;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        backTo={backTo}
        breadcrumbs={[{ label: 'Accounting' }, { label: 'Programs', to: backTo }]}
        title={groupKey}
        subtitle="Event contribution for this program: where the money goes, how the margin moved, and the events that made the least."
        size="compact"
      />
      <ProgramsFilters {...filters} />
      <ContributionNotes />
      {detail.isError ? (
        <ErrorNotice title="Program unavailable" message={errorMessage(detail.error)} />
      ) : detail.isPending ? (
        <SkeletonTable label="Loading the program…" />
      ) : detail.data.metrics.events === 0 ? (
        <Panel>
          <EmptyState title="No events in this window" message="This program has no signed events with a statement in these dates. Widen the dates or switch to all events." />
        </Panel>
      ) : (
        <>
          <DetailTiles detail={detail.data} />
          {hasCosts || hasQuarters ? (
            <div className={cn('mb-4 grid gap-4', hasCosts && hasQuarters && 'lg:grid-cols-2')}>
              {hasCosts ? <CostByCategory detail={detail.data} /> : null}
              {hasQuarters ? <MarginByQuarter detail={detail.data} /> : null}
            </div>
          ) : null}
          <ProgramEventsTable
            events={detail.data.events}
            target={detail.data.targetMargin}
            showProgram={false}
            defaultSort={EVENT_DEFAULT_SORT.byMargin}
            searchLabel={`Search ${groupKey} events`}
          />
        </>
      )}
    </section>
  );
}
